/**
 * Hook : feature flags SERVEUR centralisés.
 * - Fetch au login + polling 60s
 * - Fallback : flags locaux (localStorage) utilisés UNIQUEMENT si l'API est inaccessible
 * - Les flags serveur font autorité : ils remplacent les flags locaux
 *
 * Délai max de prise en compte client-side : 60s après changement serveur.
 * Pour une prise en compte immédiate, l'utilisateur peut recharger (location.reload).
 */
import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { isFeatureEnabled as isLocalFlagEnabled } from "@/lib/featureFlags";

const POLL_INTERVAL_MS = 60_000;
let _cache = null;
let _cacheAt = 0;
const _listeners = new Set();

export async function refreshServerFlags() {
  try {
    const r = await api.get("/config/feature-flags");
    _cache = r.data;
    _cacheAt = Date.now();
    _listeners.forEach((cb) => { try { cb(_cache); } catch { } });
    return _cache;
  } catch (err) {
    // Pas de spam en cas d'erreur réseau
    return _cache;
  }
}

export function getCachedServerFlags() {
  return _cache;
}

/** Un flag est "actif" (fonctionnalité utilisable) si l'état serveur est 'enabled'. */
export function isServerFlagEnabled(name) {
  if (!_cache?.flags) return isLocalFlagEnabled(name);
  const f = _cache.flags[name];
  if (!f) return isLocalFlagEnabled(name); // si le flag n'est pas visible pour ce rôle, fallback local
  return f.state === "enabled";
}

/** Un flag est "en lecture seule" (consulter OK, pas d'opération d'écriture). */
export function isServerFlagReadOnly(name) {
  if (!_cache?.flags) return false;
  const f = _cache.flags[name];
  if (!f) return false;
  return f.state === "new_ops_suspended" || f.state === "read_only";
}

/** Un flag est "masqué" (UI à cacher). */
export function isServerFlagHidden(name) {
  if (!_cache?.flags) return !isLocalFlagEnabled(name);
  const f = _cache.flags[name];
  if (!f) return !isLocalFlagEnabled(name);
  return f.state === "hidden";
}

/** Hook React : refresh initial + polling. */
export function useFeatureFlags() {
  const [state, setState] = useState(_cache);

  useEffect(() => {
    const sub = (v) => setState(v);
    _listeners.add(sub);
    // 1er fetch si pas de cache ou cache trop vieux
    if (!_cache || Date.now() - _cacheAt > POLL_INTERVAL_MS) {
      refreshServerFlags();
    } else {
      setState(_cache);
    }
    const t = setInterval(refreshServerFlags, POLL_INTERVAL_MS);
    return () => {
      _listeners.delete(sub);
      clearInterval(t);
    };
  }, []);

  const refresh = useCallback(() => refreshServerFlags(), []);
  return { flags: state, refresh };
}

/** Reset au logout (vide le cache pour que le prochain user charge ses propres flags). */
export function clearFeatureFlagsCache() {
  _cache = null;
  _cacheAt = 0;
  _listeners.forEach((cb) => { try { cb(null); } catch { } });
}
